import React, { useEffect, ReactNode } from "react";
import { NextRouter, useRouter } from "next/router";
import { IntercomProvider, useIntercom } from "react-use-intercom";
import { useTeamConfig } from "../hooks/useTeamConfig";
import { useAppStore } from "../store";
import { selectUser } from "../store/data/user";
import { purchaseSeatsTabUIDs } from "../config/constants";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

const APP_ID = "xol8gfkc";

function calcPaddings(router: NextRouter) {
  if (
    router.query.purchaseSeatTab == purchaseSeatsTabUIDs.findGames ||
    router.query.purchaseSeatTab == purchaseSeatsTabUIDs.chooseSeats ||
    router.query.purchaseSeatTab == purchaseSeatsTabUIDs.checkout
  ) {
    return {
      horizontalPadding: 20,
      verticalPadding: 160,
    };
  }
  return {
    horizontalPadding: 20,
    verticalPadding: 20,
  };
}

const IntercomHandlerComponent = () => {
  const { md: mdDown } = useCssMediaQuery("down");
  const router = useRouter();
  const { teamSchema, intercomConfigs } = useTeamConfig();
  const { isIntercomEnabled } = intercomConfigs;
  const user = useAppStore(selectUser);
  const intercom = useIntercom();
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Initializing intercom
  useEffect(() => {
    if (isIntercomEnabled) {
      let currentUrl = window.location.href;
      const bootConfig: any = {
        customAttributes: { teamSchema, currentUrl },
        alignment: "right",
      };

      if (mdDown) {
        bootConfig.hideDefaultLauncher = true;
        bootConfig.customLauncherSelector = "#custom-intercom-launcher";
      }

      intercom.boot(bootConfig);
      setIsInitialized(true);
    }
  }, []);

  // updating position of intercom, works only desktop
  useEffect(() => {
    if (isInitialized) {
      let paddings = calcPaddings(router);
      intercom.update({ ...paddings });
    }
  }, [router, isInitialized]);

  // updating user data after login
  useEffect(() => {
    if (user && isIntercomEnabled && isInitialized) {
      intercom.update({
        email: user?.email,
        userId: `${teamSchema}-${String(user?.userId)}`,
        // phone: user?.phoneNumber,
        customAttributes: {
          accountId: user?.accountId,
        },
      });
    }
  }, [user]);

  // handling user logout and resetting intercom session
  useEffect(() => {
    const handleUserLogout = () => {
      try {
        if (isIntercomEnabled && isInitialized) {
          intercom.shutdown();
          intercom.boot();
        }
      } catch (error) {
        console.log(error);
      }
    };
    window.addEventListener("userLogout", handleUserLogout);
    return () => {
      window.removeEventListener("userLogout", handleUserLogout);
    };
  }, []);

  // handling router change event
  useEffect(() => {
    const handleRouteChange = (url: any) => {
      if (isIntercomEnabled) {
        let currentUrl = window.location.href;
        intercom.update({ customAttributes: { currentUrl } });
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, []);

  return <></>;
};

export function IntercomCtxProvider({ children }: { children: ReactNode }) {
  return (
    <IntercomProvider appId={APP_ID}>
      <IntercomHandlerComponent />
      {children}
    </IntercomProvider>
  );
}
